import { Header, Text, View } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { CardModal } from 'components/card-modal';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { formatDate, t } from 'state/intl/translations';
import { primitive } from 'styles/constants';
import { routes } from 'utils/routing';

import { LoyaltyBadgeImage } from './loyalty-badge-image';
import { ILoyaltyBadge } from './types';

const LoyaltyBadgeDetails = ({
  badge,
  showCelebration,
}: {
  badge: ILoyaltyBadge;
  showCelebration: boolean;
}) => {
  const router = useRouter();

  const footerButtonLabel =
    !badge.earnedAt && badge.lockedCtaText ? badge.lockedCtaText : t('gotIt');

  const handleClose = () => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.dismissTo(routes.rewardsProfile);
  };

  const handleCtaPress = () => {
    if (!badge.earnedAt && badge.lockedCtaLink) {
      router.dismissTo(badge.lockedCtaLink);
      return;
    }

    handleClose();
  };

  const actionButtonStyleProps = badge.earnedAt
    ? { variant: ActionButtonVariants.OUTLINE }
    : {
        backgroundColor: primitive.$meltyYellow,
        _text: { color: primitive.$bbqBrown },
        _hover: { backgroundColor: primitive.$meltyYellowDark45 },
        _pressed: { backgroundColor: primitive.$meltyYellowDark40 },
        color: primitive.$bbqBrown,
      };

  return (
    <CardModal
      alwaysShowHeader
      webAnimate
      standardPadding
      headerText="Badges"
      onDismiss={handleClose}
      wrappedChildrenStyle={{
        paddingTop: 32,
        paddingHorizontal: 24,
        gap: 32,
        backgroundColor: primitive.$mayoEggWhite,
      }}
      footer={
        <ActionButton
          fullWidth
          accessibilityRole="link"
          onPress={handleCtaPress}
          accessibilityLabel={footerButtonLabel}
          {...actionButtonStyleProps}
        >
          {footerButtonLabel}
        </ActionButton>
      }
    >
      <View alignItems="center" gap={6}>
        <LoyaltyBadgeImage
          badge={badge}
          style={styles.badgeImage}
          showCelebration={showCelebration}
        />

        <View gap={2} accessible>
          <Header
            textAlign="center"
            fontSize="xl"
            margin={0}
            accessible
            focusable
            accessibilityLevel={2}
          >
            {badge.name}
          </Header>
          {!!badge.description && (
            <SanityBlockRenderer
              content={badge.description}
              textProps={{ style: styles.description, focusable: true, accessible: true }}
            />
          )}
        </View>
      </View>
      {badge.earnedAt && (
        <Text style={styles.earnedAtMessage} accessible focusable>
          {t('achievedOnDate', {
            date: formatDate(badge.earnedAt),
            defaultValue: 'Achieved on {date}',
          })}
        </Text>
      )}
    </CardModal>
  );
};

const styles = StyleSheet.create({
  badgeImage: {
    width: 250,
    height: 250,
  },
  description: {
    fontSize: primitive.fontSizes.md,
    textAlign: 'center',
  },
  earnedAtMessage: {
    fontSize: primitive.fontSizes.sm,
    textAlign: 'center',
  },
});

export default LoyaltyBadgeDetails;
